// Halls page: one card per element hall with its Hall Keeper, your registered
// team (and which members count now), the challenge button with its 24 h
// clock, and the podium of registered teams by power. Duel results land in
// the hunt log and the duel list below the cards; nothing here pops up.
import { HALLS, HALL_IDS, KEEPER_TITLE, fitsHall, hallName, type HallDuelMsg, type HallView, type HallsDTO } from '../shared/halls.ts';
import { ARENA, type ArenaCreature } from '../shared/arena.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { LEVEL_SLACK, canFight } from '../shared/xp.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { esc, full, cname, left, ago, elChip } from './fmt.ts';
import { trainerFace } from './outfits.ts';
import { playerIcon } from './playericon.ts';
import { spriteUrl } from './assets.ts';
import { logLine } from './hud.ts';
import { battleLog, teamFace } from './arena.ts';
import { synergyChips } from './synergy.ts';
import { heldBadge, heldChip } from './held.ts';
import { asFighter } from '../shared/social.ts';
import { PVP } from '../shared/pvp.ts';
import { BOARD, type BoardRow } from '../shared/board.ts';
import { boardPrep } from './boardview.ts';
import { mySprite } from './trainerart.ts';
import { teamStrip } from './teampick.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { psecTabs, psecWrap, openSection } from './psec.ts';
import { boardOn, frontLabel, pickFrom, reachBadge, rowClick, rowToggle, rowsOut, setRow, shownRows, type RowPick } from './rows.ts';

/** A creature as a hall fights it: at Lv 150, up or down (shared/pvp.ts atFixedLevel). */
const asHallFighter = (c: CreatureDTO): ArenaCreature => ({ ...asFighter(c), level: PVP.fixedLevel });

export const H = {
  data: null as HallsDTO | null,
  /** Open duel logs by id (null while loading). */
  logs: new Map<number, HallDuelMsg | null>(),
  /** The team being edited: its hall, the creature ids in order, and rows (the board; null: defaultRows). */
  edit: null as null | { el: Element; ids: number[]; rows: RowPick },
  /** The hall shown (a tile on the row of halls); null: yours, else the first. */
  pick: null as Element | null,
  /** Desktop's side column: the hall's ranking or your duels (phones and tablets use the section tabs). */
  side: 'rank' as 'rank' | 'duels',
};

/** A hall's sections: its keeper, your team, its ranking, and your duels (a phone shows one at a time, client/psec.ts). */
const HALL_SECS = ['keeper', 'team', 'rank', 'duels'];

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindHalls(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

export function loadHalls() { send({ t: 'halls' }); }
export function onHalls(d: HallsDTO) { H.data = d; rerender(); }
export function onHallDuel(m: HallDuelMsg) { H.logs.set(m.id, m); rerender(); }

/** A challenge finished (yours, or one against your hall): a log line, and fresh data. */
export function onHallResult(d: HallDuelMsg['summary']) {
  const hall = esc(hallName(d.el));
  const text = d.challenged
    ? `${hall}: you ${d.win ? 'beat' : 'lost to'} ${esc(d.opp)}${d.took ? ` and are now its ${KEEPER_TITLE}` : ''}`
    : `${hall}: ${esc(d.opp)} challenged you and ${d.win ? 'lost; you keep the hall' : `won; ${esc(d.opp)} is now its ${KEEPER_TITLE}`}`;
  logLine(text, d.win ? 'gold' : '');
  loadHalls();
}

export function hallsHead() {
  const held = S.trainer?.halls ?? [];
  return {
    meta: held.length ? `${KEEPER_TITLE} of ${held.map((e) => esc(ELEMENTS[e as Element]?.name ?? e)).join(', ')}` : '',
    actions: '<button class="btn small" data-act="halls-refresh">Refresh</button>',
  };
}

/** The rows a hall team's fighting members stand on, by id (its saved rows, else defaultRows). */
function hallRows(fights: CreatureDTO[], pick: RowPick): Map<number, BoardRow> {
  const team = fights.map(asHallFighter);
  const rows = shownRows(team, pick);
  return new Map(team.map((c, k) => [c.id, rows[k]]));
}
const fightsOf = (picked: CreatureDTO[], el: Element) => picked.filter((c) => !c.listed && canFight(c, S.trainer!.level) && fitsHall(c.species, el));

const mons = (team: { species: string; level: number; aura?: boolean }[]) => team.length ? team.map((c) => teamFace(c, '', false, undefined, PVP.fixedLevel)).join('') : '<span class="muted">No creatures count</span>';

export function hallsPane(): string {
  const d = H.data;
  const t = S.trainer!;
  if (!d) return '<p class="hint">Loading the halls…</p>';
  const held = t.halls ?? [];
  const note = d.admin ? '<p class="hint">Admin accounts can\'t register.</p>' : !d.unlocked ? `<p class="hint">Opens at trainer Lv ${HALLS.unlockLevel}</p>` : held.length ? `<p class="good-text">${KEEPER_TITLE}: ${held.map((e) => elChip(e)).join(' ')}</p>` : '';
  // Keeping a hall shows in the page's header line and on its tile, so only the other notes need a panel.
  const intro = note && !held.length ? `<div class="panel hl-intro">${note}</div>` : '';
  const pick = H.pick && d.halls.some((h) => h.el === H.pick) ? H.pick
    : d.halls.find((h) => h.keeper?.me)?.el ?? d.halls.find((h) => h.mine)?.el ?? d.halls[0]?.el;
  const h = d.halls.find((x) => x.el === pick);
  if (!h) return intro;
  // Editing a team takes the whole page, so the board can be big without scrolling.
  if (H.edit?.el === h.el) return `<article class="panel hl-card hl-editor" style="--hc:${ELEMENTS[h.el].color}" data-hall="${h.el}" data-key="hl-editor">
    <header class="hl-head"><h3>${esc(hallName(h.el))}</h3>${elChip(h.el)}</header>${editorHtml(h.el)}</article>`;
  const tiles = `<nav class="hl-tiles" aria-label="Halls">${d.halls.map((x) => hallTile(x, x.el === pick)).join('')}</nav>`;
  const tabs = psecTabs('halls', [{ key: 'keeper', title: 'Keeper' }, { key: 'team', title: 'Your team' }, { key: 'rank', title: 'Ranking' }, { key: 'duels', title: 'Duels' }]);
  // On desktop the ranking and your duels take turns beside the hall, so neither pages down to a row or two.
  const side = `<nav class="hl-side-tabs psec-tabs all" style="--n:2">${(['rank', 'duels'] as const).map((k) => `<button class="${H.side === k ? 'on' : ''}" data-act="halls-side" data-id="${k}" aria-pressed="${H.side === k}">${k === 'rank' ? 'Ranking' : 'Your duels'}</button>`).join('')}</nav>`;
  return `${intro}${tiles}${tabs}<div class="hl-detail">${hallCard(h, d)}
    <section class="panel ar-sec hl-side" data-side="${H.side}">${side}
    ${psecWrap('halls', 'rank', HALL_SECS, `<div class="hl-rank">${podiumHtml(h)}</div>`)}
    ${psecWrap('halls', 'duels', HALL_SECS, `<div class="hl-duels"><h3>Your challenges and defenses</h3>${duelsHtml(d)}</div>`)}</section></div>`;
}

/** One hall on the row of halls: its element, its keeper's face, and a mark when it is yours or you have a team in it. */
function hallTile(h: HallView, on: boolean) {
  const k = h.keeper;
  const mark = k?.me ? `<i class="hl-t-mine" title="You are its ${KEEPER_TITLE}"></i>` : h.mine ? '<i class="hl-t-in" title="Your team is registered"></i>' : '';
  return `<button class="hl-tile${on ? ' on' : ''}${k?.me ? ' kept' : ''}" style="--hc:${ELEMENTS[h.el].color}" data-act="halls-pick" data-id="${h.el}" aria-pressed="${on}" aria-label="${esc(hallName(h.el))}">
    ${elChip(h.el)}<span class="hl-t-k">${k ? trainerFace(k.outfit) : '<span class="hl-t-none">—</span>'}</span>${mark}</button>`;
}

function hallCard(h: HallView, d: HallsDTO) {
  const el = ELEMENTS[h.el];
  const now = serverNow();
  const k = h.keeper;
  const keeper = k
    ? `<div class="hl-keeper"><div class="ar-l-head">${trainerFace(k.outfit)}<b>${esc(k.name)}</b>${k.me ? ' <span class="pill lead">You</span>' : ''}<span class="muted">${KEEPER_TITLE} for ${ago(k.since)}</span><span class="spacer"></span><span class="muted">Power ${full(k.power)}</span></div>
      <div class="ar-team">${mons(k.team)}</div>${synergyChips(k.team)}</div>`
    : `<p class="muted hl-vacant">Vacant</p>`;
  return `<article class="panel hl-card" style="--hc:${el.color}" data-hall="${h.el}">
    <header class="hl-head"><h3>${esc(hallName(h.el))}</h3>${elChip(h.el)}${k?.me ? `<span class="pill amber">${KEEPER_TITLE}</span>` : ''}</header>
    ${psecWrap('halls', 'keeper', HALL_SECS, keeper)}
    ${psecWrap('halls', 'team', HALL_SECS, mineHtml(h, d, now) || `<p class="hint">${d.admin ? 'Admin accounts can\'t register.' : `Opens at trainer Lv ${HALLS.unlockLevel}`}</p>`)}
  </article>`;
}

function mineHtml(h: HallView, d: HallsDTO, now: number) {
  if (d.admin || !d.unlocked) return '';
  const m = h.mine;
  if (!m) {
    const fits = [...S.creatures.values()].some((c) => !c.listed && fitsHall(c.species, h.el));
    return `<div class="hl-mine"><button class="btn small" data-act="halls-edit" data-id="${h.el}"${fits ? '' : ` disabled title="You have no ${ELEMENTS[h.el].name} creatures"`}>Register a team</button></div>`;
  }
  const t = S.trainer!;
  const rows = boardOn() ? hallRows(m.counting.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c), pickFrom(m.ids, m.rows)) : null;
  const faces = m.ids.map((id) => {
    const c = S.creatures.get(id);
    if (!c) return '';
    const counts = m.counting.includes(id);
    const row = rows?.get(id);
    const why = counts ? '' : c.listed ? 'listed on the market' : !canFight(c, t.level) ? `more than ${LEVEL_SLACK} levels above you` : !fitsHall(c.species, h.el) ? `no longer ${ELEMENTS[h.el].name}` : 'same species as an earlier member';
    return `<span class="ar-mon${c.rolls.aura ? ' aura' : ''}${counts ? '' : ' out'}${row === 'front' ? ' fr' : ''}" title="${esc(cname(c))} · Lv ${full(c.level)}${counts ? ` (fights at Lv ${PVP.fixedLevel})` : ` · doesn't count: ${why}`}${row ? ` · ${row === 'front' ? 'front row' : 'back row'}` : ''}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt=""><span>Lv ${full(c.level)}</span>${heldBadge(c)}</span>`;
  }).join('');
  const gone = m.ids.length - m.ids.filter((id) => S.creatures.has(id)).length;
  const isKeeper = !!h.keeper?.me;
  let challenge = '';
  if (!isKeeper && h.keeper) {
    const wait = h.readyAt - now;
    challenge = wait > 0
      ? `<button class="btn small" disabled>Challenge in ${left(wait)}</button>`
      : `<button class="btn small primary" data-act="halls-challenge" data-id="${h.el}"${m.counting.length ? '' : ' disabled title="None of your creatures count"'}>Challenge ${esc(h.keeper.name)}</button>`;
  }
  return `<div class="hl-mine"><div class="ar-l-head"><b>Your team</b><span class="muted">${m.rank ? `#${m.rank} · ` : ''}power ${full(m.power)} · ${m.counting.length} of ${m.ids.length} count${gone ? ` · ${gone} no longer yours` : ''}</span></div>
    <div class="ar-team">${faces}</div>${mineChips(m.counting)}
    <div class="actions">${challenge}<button class="btn small" data-act="halls-edit" data-id="${h.el}">Edit team</button><button class="btn small" data-act="halls-withdraw" data-id="${h.el}">Withdraw</button></div></div>`;
}

/** Synergy chips for the members of your hall team that count (they are the ones that fight). */
function mineChips(ids: number[]) {
  const team = ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  return synergyChips(team, undefined, (i) => cname(team[i]));
}

/** Each hall's ground on the board: the land that element lives in. */
const HALL_SCENE: Record<Element, string> = { flame: 'ember', tide: 'coast', grove: 'meadow', storm: 'peak', stone: 'crag', frost: 'snow', shade: 'graveyard', light: 'shrine' };

function editorHtml(el: Element) {
  const e = H.edit!;
  const picked = e.ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const fights = fightsOf(picked, el);
  const rows = hallRows(fights, e.rows);
  const board = boardOn() && fights.length ? `<div class="ar-board">${boardPrep({
    key: `halls-edit:${el}`,
    mine: fights.map((c) => ({ id: c.id, species: c.species, level: c.level, aura: c.rolls.aura, row: rows.get(c.id) ?? 'back', held: c.held })),
    theirs: [],
    labels: ['You', ''],
    scene: HALL_SCENE[el] ?? 'plain',
    trainers: [mySprite(), null],
    onRows: (picks) => { if (H.edit) { H.edit.rows = picks; rerender(); } },
  })}</div>` : '';
  return `<div class="ar-edit hl-mine">
    ${board}
    <div class="tn-sec"><h4>Team <span class="muted">${picked.length}/${HALLS.teamMax}</span></h4>${synergyChips(picked, undefined, (i) => cname(picked[i]))}<span class="spacer"></span><button class="btn small" data-act="halls-team">Change team</button></div>
    ${teamStrip(picked.map(asFighter), HALLS.teamMax, 'halls-team')}
    <div class="actions"><button class="btn small primary" data-act="halls-save"${picked.length ? '' : ' disabled'}>Save team</button><button class="btn small" data-act="halls-cancel">Cancel</button></div></div>`;
}

function podiumHtml(h: HallView) {
  if (!h.podium.length) return '<p class="hint">No teams registered yet.</p>';
  return `<div class="hl-podium"><table class="tbl2"><thead><tr><th>#</th><th>Trainer</th><th>Team</th><th class="num">Power</th></tr></thead><tbody data-fit="hall-rank">
    ${h.podium.map((r) => `<tr class="${r.me ? 'on' : ''}"><td>${r.rank}</td><td class="hl-who">${playerIcon({ outfit: r.outfit, size: 'md' })}${esc(r.name)}${r.keeper ? ' <span class="pill amber">Keeper</span>' : ''}</td><td><span class="hl-mini">${r.team.map((c) => teamFace(c, '', false, undefined, PVP.fixedLevel)).join('')}</span></td><td class="num">${full(r.power)}</td></tr>`).join('')}</tbody></table></div>`;
}

function duelsHtml(d: HallsDTO) {
  if (!d.duels.length) return '<p class="hint">No hall duels yet.</p>';
  return `<ul class="ar-recent" data-fit="hall-duels">${d.duels.map((x) => {
    const open = H.logs.has(x.id);
    const what = x.challenged ? `You challenged ${esc(x.opp)}` : `${esc(x.opp)} challenged you`;
    const out = x.took ? (x.challenged ? 'took the hall' : 'lost the hall') : (x.challenged ? 'hall unchanged' : 'kept the hall');
    return `<li class="${x.win ? 'win' : 'loss'}"><button class="ar-m" data-act="halls-duel" data-id="${x.id}" aria-expanded="${open}">
      <b class="${x.win ? 'good' : 'bad'}">${x.win ? 'Won' : 'Lost'}</b>${elChip(x.el)}<span>${what}</span><span class="muted">${out}</span><span class="spacer"></span><span class="muted">${ago(x.ts)} ago</span></button>
      ${open ? duelLog(x.id) : ''}</li>`;
  }).join('')}</ul>`;
}

function duelLog(id: number) {
  const x = H.logs.get(id);
  if (!x) return '<p class="hint">Loading the duel log…</p>';
  return battleLog(x.result, x.side);
}

/** Clicks on data-act="halls-…". Returns true when handled. */
export function hallsClick(act: string, id: string): boolean {
  switch (act) {
    case 'halls-refresh': loadHalls(); return true;
    case 'halls-challenge': send({ t: 'hallChallenge', el: id }); return true;
    case 'halls-withdraw': send({ t: 'hallWithdraw', el: id }); return true;
    case 'halls-side': H.side = id === 'duels' ? 'duels' : 'rank'; break;
    case 'halls-pick': if ((HALL_IDS as readonly string[]).includes(id)) { H.pick = id as Element; H.edit = null; } break;
    case 'halls-edit': {
      openSection('halls', 'team');
      const h = H.data?.halls.find((x) => x.el === id);
      if (h) H.edit = { el: h.el, ids: [...(h.mine?.ids ?? [])].filter((cid) => S.creatures.has(cid)), rows: h.mine ? pickFrom(h.mine.ids, h.mine.rows) : null };
      break;
    }
    case 'halls-cancel': H.edit = null; break;
    case 'halls-team': {
      const e = H.edit;
      if (!e) break;
      const lv = S.trainer!.level;
      openTeamBuilder({
        title: `${hallName(e.el)} team`, ids: e.ids, max: HALLS.teamMax, unique: true, name: `hall:${e.el}`, key: String(lv),
        ok: (c) => !c.listed && canFight(c, lv) && fitsHall(c.species, e.el), asFought: asHallFighter,
        onSave: (ids) => { if (H.edit) { H.edit.ids = ids; rerender(); } },
      });
      return true;
    }
    case 'halls-add': {
      const sel = document.querySelector('[data-halls-add]') as HTMLSelectElement | null;
      const cid = Number(sel?.value);
      if (H.edit && cid && !H.edit.ids.includes(cid) && H.edit.ids.length < HALLS.teamMax) H.edit.ids.push(cid);
      break;
    }
    case 'halls-up': case 'halls-down': {
      if (!H.edit) break;
      const i = Number(id), j = act === 'halls-up' ? i - 1 : i + 1;
      const ids = H.edit.ids;
      if (j >= 0 && j < ids.length) [ids[i], ids[j]] = [ids[j], ids[i]];
      break;
    }
    case 'halls-remove': if (H.edit) H.edit.ids.splice(Number(id), 1); break;
    case 'halls-row': {
      const x = rowClick(id);
      if (!H.edit || !x) break;
      const picked = H.edit.ids.map((cid) => S.creatures.get(cid)).filter((c): c is CreatureDTO => !!c);
      H.edit.rows = setRow(fightsOf(picked, H.edit.el).map(asFighter), H.edit.rows, x.id, x.row);
      break;
    }
    case 'halls-save': {
      if (!H.edit) break;
      const picked = H.edit.ids.map((cid) => S.creatures.get(cid)).filter((c): c is CreatureDTO => !!c);
      const ids = picked.map((c) => c.id);
      const rows = rowsOut(ids, H.edit.rows, hallRows(fightsOf(picked, H.edit.el), H.edit.rows));
      send({ t: 'hallRegister', el: H.edit.el, ids, ...(rows ? { rows } : {}) });
      H.edit = null; break;
    }
    case 'halls-duel': {
      const did = Number(id);
      if (H.logs.has(did)) H.logs.delete(did); else { H.logs.set(did, null); send({ t: 'hallDuel', id: did }); }
      break;
    }
    default: return false;
  }
  rerender();
  return true;
}

/** Help page section. Numbers come from shared/halls.ts and shared/arena.ts. */
export function hallsHelp(): string {
  return `<h4>Element Halls</h4><p>There is one hall for each element (${HALL_IDS.map((e) => ELEMENTS[e].name).join(', ')}); find them on the Halls page. Each hall has one ${KEEPER_TITLE}.</p>
    <ul><li><b>Registering</b>: from trainer level ${HALLS.unlockLevel}, register a team of 1 to ${HALLS.teamMax} of your creatures at a hall. Every member must have the hall's element (a dual-element creature fits both of its halls), and each species can be on the team once; an aura is the same species. You can register at every hall. A registered team isn't locked: its creatures keep hunting, fighting in the arena and trading.</li>
      <li><b>Which members count</b>: only creatures you still own, not listed on the market and within ${LEVEL_SLACK} levels of you. If a member ascends into a species already on the team, it stops counting; the earlier member keeps its place.</li>
      <li><b>Keeper bonus</b>: the ${KEEPER_TITLE} deals +${Math.round((HALLS.keeperMult - 1) * 100)}% damage with creatures of the hall's element, on the hunt and against bosses (the boss estimates count it too). It doesn't apply in hall duels, the arena or Guild War. It shows in the Boosts panel.</li>
      <li><b>One hall each</b>: a trainer keeps one hall at a time. Win a second by challenge and you give up the first, which goes to the earliest registration there that can hold it. A vacant hall skips trainers who keep another.</li>
      <li><b>Taking a hall</b>: a vacant hall goes to the first team registered there. After that the keeper holds it until beaten in a challenge. If the keeper withdraws, or none of their team counts any more, the hall goes to the earliest registration that still counts. Admin accounts can't register or keep a hall.</li>
      <li><b>Challenges</b>: any registered trainer can challenge the keeper once every ${HALLS.cooldownMs / 3_600_000} hours per hall; each hall has its own clock, and the keeper doesn't need to be online. ${boardOn()
    ? `The duel is the arena's battle, on the board: each team stands on the rows it registered with (a team registered without rows puts its bulkiest creatures in front). The Keeper's team is public, its rows are not. Every creature fights at Lv ${PVP.fixedLevel}, up or down; rolls count as in the arena. At ${BOARD.capMs / 1000} s the side with more creatures standing wins, then the larger share of HP left, and the keeper keeps the hall on a tie.`
    : `The duel is the arena's battle: front creatures fight one on one in team order, and a knocked-out creature is replaced by the next. The Keeper's order is public: the hall shows their team in the order it fights. Every creature fights at Lv ${PVP.fixedLevel}, up or down; rolls count as in the arena. From ${ARENA.overtimeMs / 1000} s all damage doubles every 10 s; at ${ARENA.battleCapMs / 1000} s the side with more creatures standing wins, then the larger share of HP left, and the keeper keeps the hall on a tie.`} Win and the hall is yours; the result goes to the hunt log, and a line in World says when a hall changes hands. Every duel's log is on the Halls page.</li>
      <li><b>Podium</b>: each hall lists the top ${HALLS.podium} registered teams by power, the summed power of the members that count, at Lv ${PVP.fixedLevel}.</li></ul>`;
}

